import { useEffect, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { Box, Button, Stack, Typography } from '@mui/material';

import { useMutation } from '@redwoodjs/web';

import LeftIcon from 'src/assets/images/icons/ArrowLeftSVG';
import RightIcon from 'src/assets/images/icons/ArrowRightSVG';
import { ModalBox } from 'src/components/ModalContent/Modal';
import { UPDATE_PRICING_OFFER_PARTNERS_MUTATION } from 'src/graphql/mutations/pricing.mutation';
import { GET_PARTNERS } from 'src/graphql/queries/partners.query';

import OffersCard from '../AddPartnerPage/components/PricingInformation/OffersCard/OffersCard';

interface Partner {
    id: number;
    name: string;
    index: number;
}

interface PricingOfferPartnersModal {
    open: boolean;
    handleCloseModal: () => void;
    selectedPartners: Partner[];
    setSelectedPartners: (partners: Partner[]) => void;
    offerId?: number;
}

export const TerminalPricingOfferPartnersModal = ({
    open,
    handleCloseModal,
    selectedPartners,
    setSelectedPartners,
    offerId,
}: PricingOfferPartnersModal) => {
    const [partners, setPartners] = useState([]);
    const [assignedPartners, setAssignedPartners] = useState(selectedPartners);
    const [selected, setSelected] = useState<Partner | null>(null);
    const [getPartners] = useLazyQuery(GET_PARTNERS);
    const [updatePricingOfferPartners] = useMutation(UPDATE_PRICING_OFFER_PARTNERS_MUTATION, {
        onCompleted: () => {
            setSelectedPartners(assignedPartners);
            handleCloseModal();
        },
    });

    const getPartnersData = async () => {
        const makeRequest = await getPartners();
        if (makeRequest) {
            const { partners } = makeRequest.data;
            const partnerList = partners.map(({ id, firstName, lastName, business }) => ({
                id,
                name: business?.legalName || `${firstName} ${lastName}`,
            }));
            if (selectedPartners.length >= 1) {
                const assignedPartnerIds = selectedPartners.map(({ id }) => id);
                const updatedPartners = partnerList.reduce((accumulator, partner) => {
                    if (!assignedPartnerIds.includes(partner.id)) {
                        accumulator = [...accumulator, partner];
                    }
                    return accumulator;
                }, []);
                setAssignedPartners(selectedPartners);
                setPartners(updatedPartners);
            } else {
                setPartners(partnerList);
                setAssignedPartners([]);
            }
        }
    };

    useEffect(() => {
        const makeRequest = () => getPartnersData();
        makeRequest();
    }, [selectedPartners]);

    const handleOnSelected = (partner: any, index: number) => {
        const selectedPartner = { ...partner, index };
        setSelected(selectedPartner);
    };

    const handleAssignedPartner = () => {
        if (selected) {
            const index = assignedPartners.findIndex((partner) => partner.id === selected.id);
            if (index >= 0) return;
            setAssignedPartners([selected, ...assignedPartners]);
            partners.splice(selected.index, 1);
            setPartners(partners);
            setSelected(null);
        }
    };

    const handleUnassignedPartner = () => {
        if (selected) {
            const index = partners.findIndex((partner) => partner.id === selected.id);
            if (index >= 0) return;
            partners.splice(selected.index, 0, selected);
            setPartners(partners);
            const filteredAssignedPartners = assignedPartners.filter(
                (partner) => partner.id !== selected.id
            );
            setAssignedPartners(filteredAssignedPartners);
            setSelected(null);
        }
    };

    const handleSavePricingOfferPartners = async () => {
        if (offerId) {
            await updatePricingOfferPartners({
                variables: {
                    input: {
                        id: offerId,
                        partners: assignedPartners.map(({ id }) => id),
                    },
                },
            });
        } else {
            setSelectedPartners(assignedPartners);
            handleCloseModal();
        }
    };

    return (
        <ModalBox
            open={open}
            handleCloseModal={handleCloseModal}
            title="Assign Partner"
            widthSize="860px"
        >
            <Stack>
                <Box display="flex" justifyContent="space-between">
                    <Box width="300px" margin="20px">
                        <OffersCard title="Partner List">
                            <Box
                                sx={{
                                    mt: 1,
                                    width: '95%',
                                    height: '255px',
                                    overflowY: 'scroll',
                                }}
                            >
                                {partners.map((partner, index) => (
                                    <Typography
                                        key={index}
                                        onClick={() => handleOnSelected(partner, index)}
                                        sx={{
                                            fontSize: '18px',
                                            p: 1,
                                            ml: 1.5,
                                            cursor: 'pointer',
                                            ':hover': { color: '#F39E4E' },
                                            color: `${
                                                selected?.id === partner.id ? '#F39E4E' : '#000'
                                            }`,
                                        }}
                                    >
                                        {partner.name}
                                    </Typography>
                                ))}
                            </Box>
                        </OffersCard>
                    </Box>

                    <Box>
                        <Stack spacing={10} marginTop="120px">
                            <LeftIcon
                                style={{ cursor: 'pointer' }}
                                onClick={handleUnassignedPartner}
                            />
                            <RightIcon
                                style={{ cursor: 'pointer' }}
                                onClick={handleAssignedPartner}
                            />
                        </Stack>
                    </Box>

                    <Box width="300px" margin="20px">
                        <OffersCard title="Assigned Partner">
                            <Box
                                sx={{
                                    mt: 1,
                                    width: '95%',
                                    height: '255px',
                                    overflowY: 'scroll',
                                }}
                            >
                                {assignedPartners.map((partner, index) => (
                                    <Typography
                                        key={index}
                                        onClick={() => handleOnSelected(partner, partner.index)}
                                        sx={{
                                            fontSize: '18px',
                                            p: 1,
                                            ml: 1.5,
                                            cursor: 'pointer',
                                            ':hover': { color: '#F39E4E' },
                                            color: `${
                                                selected?.id === partner?.id ? '#F39E4E' : '#000'
                                            }`,
                                        }}
                                    >
                                        {partner.name}
                                    </Typography>
                                ))}
                            </Box>
                        </OffersCard>
                    </Box>
                </Box>
                <Box display="flex" justifyContent="flex-end" margin="10px 0 0 0px">
                    <Box>
                        <Button
                            onClick={handleCloseModal}
                            className="reset-btn btn-medium mr-20"
                            size="medium"
                            disableElevation
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSavePricingOfferPartners}
                            className={`btn-medium mr-20 ${
                                assignedPartners.length >= 0 ? 'btn-primary' : 'btn-dark'
                            }`}
                            size="medium"
                            disableElevation
                        >
                            Save
                        </Button>
                    </Box>
                </Box>
            </Stack>
        </ModalBox>
    );
};
